/**
 * R3F component rendering auto-cannon tracers as instanced meshes.
 * Reads projectile state from ProjectileSystem module (no zustand).
 * Single InstancedMesh for all tracers across all ships.
 */

import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useGameStore } from '../../stores/gameStore';
import * as projectileSystem from './ProjectileSystem';
import { tracerVertexShader, tracerFragmentShader } from '../shaders/tracerShaders';
import {
  MAX_TRACERS,
  FIGHTER_RADIUS,
  MOON_RADIUS,
  MOON_ORBIT_RADIUS,
  PLANET_X,
  PLANET_Y,
} from '@warmancer/shared';

// Reusable objects to avoid GC
const tempMatrix = new THREE.Matrix4();
const tempPosition = new THREE.Vector3();
const tempQuaternion = new THREE.Quaternion();
const tempScale = new THREE.Vector3();
const zeroScale = new THREE.Vector3(0, 0, 0);

// Pre-computed: lay flat on XZ (Rx -90deg), then rotate around Y for direction
// Base rotation: PlaneGeometry XY → XZ plane
const baseEuler = new THREE.Euler(-Math.PI / 2, 0, 0, 'YXZ');

export function ProjectileEffects() {
  const meshRef = useRef<THREE.InstancedMesh>(null);

  const material = useMemo(
    () =>
      new THREE.ShaderMaterial({
        vertexShader: tracerVertexShader,
        fragmentShader: tracerFragmentShader,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    [],
  );

  const geometry = useMemo(() => new THREE.PlaneGeometry(0.25, 1.5), []);

  useFrame((_, delta) => {
    const mesh = meshRef.current;
    if (!mesh) return;

    const state = useGameStore.getState();
    const localShip = state.localShip;
    const remotePlayers = state.remotePlayers;
    const clientTick = state.clientTick;
    const moonAngle = state.moonAngle;

    // Spawn tracers for firing ships
    if (localShip?.alive && localShip.firing) {
      projectileSystem.trySpawn(
        localShip.x,
        localShip.y,
        localShip.rotation,
        localShip.vx,
        localShip.vy,
        localShip.entityId,
        clientTick,
      );
    }

    for (const remote of remotePlayers.values()) {
      const s = remote.state;
      if (s.alive && s.firing) {
        projectileSystem.trySpawn(s.x, s.y, s.rotation, s.vx, s.vy, s.entityId, clientTick);
      }
    }

    // Update tracer positions
    projectileSystem.update(delta);

    // Gather collision targets
    const asteroids = state.asteroidPositions ?? [];
    const shipTargets: Array<{ entityId: number; x: number; y: number; radius: number }> = [];
    if (localShip?.alive) {
      shipTargets.push({
        entityId: localShip.entityId,
        x: localShip.x,
        y: localShip.y,
        radius: FIGHTER_RADIUS,
      });
    }
    for (const remote of remotePlayers.values()) {
      const rs = remote.state;
      if (rs.alive) {
        shipTargets.push({
          entityId: rs.entityId,
          x: rs.x,
          y: rs.y,
          radius: FIGHTER_RADIUS,
        });
      }
    }

    // Moon position from angle
    const moonX = PLANET_X + Math.cos(moonAngle) * MOON_ORBIT_RADIUS;
    const moonY = PLANET_Y + Math.sin(moonAngle) * MOON_ORBIT_RADIUS;

    projectileSystem.checkCollisions(
      asteroids,
      shipTargets,
      moonX,
      moonY,
      MOON_RADIUS,
    );

    // Update instanced mesh matrices
    const tracers = projectileSystem.getTracers();
    for (let i = 0; i < tracers.length; i++) {
      const t = tracers[i];
      if (!t.active) {
        tempMatrix.compose(tempPosition, tempQuaternion, zeroScale);
        mesh.setMatrixAt(i, tempMatrix);
        continue;
      }

      // Position on XZ plane (Y=0.5 to render above the ground plane)
      tempPosition.set(t.x, 0.5, t.y);

      // Orient quad to face velocity direction on XZ plane
      // PlaneGeometry is XY. Rotate -90 around X to lay on XZ, then rotate Y for direction.
      const angle = Math.atan2(t.vy, t.vx);
      baseEuler.y = -angle + Math.PI / 2;
      tempQuaternion.setFromEuler(baseEuler);

      tempScale.set(1, 1, 1);
      tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
      mesh.setMatrixAt(i, tempMatrix);
    }

    mesh.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh
      ref={meshRef}
      args={[geometry, material, MAX_TRACERS]}
      frustumCulled={false}
    />
  );
}
